import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useBackendApiService } from '../../../services/backendApiService';
import { subscribeToAgentResults } from '../../../services/mqtt/mqttService';
import CryptoJS from 'crypto-js';
import {
  FiCommand,
  FiChevronRight,
  FiSave,
  FiList,
  FiEdit,
  FiTrash2,
  FiCheck,
  FiX,
} from 'react-icons/fi';
import { getRegistryModels, getRegistryActionsByModel } from '../../../services/registryService';

const STORAGE_KEY = 'rest_command_form_data_v3'; // Updated key for new integration
const MACROS_STORAGE_KEY = 'restMacros'; // Key for storing macros
const SAVED_COMMANDS_STORAGE_KEY = 'savedCommands'; // New preferred key for saved commands

// Predefined API command presets
// Security: do not ship presets containing device credentials, agent ids, or IP targets.
const algoApiPresets = [];

const RestCallTestPanel = ({ tenantId }) => {
  // ...other hooks...
  const [showRequestBody, setShowRequestBody] = useState(false);
  const backendApi = useBackendApiService();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  // Probe session state
  const [probeRunning, setProbeRunning] = useState(false);
  const [probeResults, setProbeResults] = useState([]);
  const [probeSessionActive, setProbeSessionActive] = useState(false);
  // Ref map to correlate MQTT results to probe entries without resubscribing
  const probeMapRef = useRef({ active: false, map: {} });

  // Registry helpers state
  const [registryModels, setRegistryModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [modelActions, setModelActions] = useState([]);
  const [selectedActionKey, setSelectedActionKey] = useState('');
  const [selectedActionMeta, setSelectedActionMeta] = useState(null);

  // Macro management state
  const [macros, setMacros] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMacroId, setEditingMacroId] = useState(null);
  const [editedMacroName, setEditedMacroName] = useState('');
  const macroNameInputRef = useRef(null);

  // Common state
  const [agentId, setAgentId] = useState('');
  const [method, setMethod] = useState('POST');
  const [ipAddress, setIpAddress] = useState('');

  // MQTT result subscription state
  const [resultSubscription, setResultSubscription] = useState(null);
  const [lastCommandId, setLastCommandId] = useState(null);
  const [restPath, setRestPath] = useState('');
  const [action, setAction] = useState('');
  const [parameters, setParameters] = useState([{ id: Date.now(), key: '', value: '' }]);

  // Algo API specific authentication options
  const [authMethod, setAuthMethod] = useState('standard');
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [nonce, setNonce] = useState('');
  const [timestamp, setTimestamp] = useState('');

  // SSL/TLS options
  const [useHttps, setUseHttps] = useState(true);
  const [verify, setVerify] = useState(false);

  // URL transformation preview
  const [urlPreview, setUrlPreview] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('none');

  useEffect(() => {
    if (selectedTemplate === 'none') return;

    const templates = {
      queryStatus: {
        action: 'QueryStatus',
        parameters: [],
      },
      startTone: {
        action: 'StartTone',
        parameters: [
          { id: Date.now(), key: 'path', value: 'bell.wav' },
          { id: Date.now() + 1, key: 'loop', value: 'false' },
        ],
      },
      stopTone: {
        action: 'StopTone',
        parameters: [],
      },
      startAnnouncement: {
        action: 'StartAnnouncement',
        parameters: [
          { id: Date.now(), key: 'path', value: 'announcement.wav' },
          { id: Date.now() + 1, key: 'repeat', value: '1' },
        ],
      },
    };

    const template = templates[selectedTemplate];
    if (template) {
      setAction(template.action);
      setParameters(template.parameters);
      toast.success(`Loaded ${template.action} template`);
    }

    // Reset template selection
    setSelectedTemplate('none');
  }, [selectedTemplate]);

  useEffect(() => {
    try {
      let previewUrl = '';
      if (ipAddress) {
        // Handle URL scheme
        if (ipAddress.startsWith('http://') || ipAddress.startsWith('https://')) {
          previewUrl = ipAddress;
        } else {
          previewUrl = useHttps ? `https://${ipAddress}` : `http://${ipAddress}`;
        }

        // Add REST path if provided
        if (restPath) {
          const formattedPath = restPath.startsWith('/') ? restPath : `/${restPath}`;
          previewUrl += formattedPath;
        } else {
          // Default path for Algo API if none provided
          previewUrl += '/api/device/action';
        }

        // Show transformation for /device/action to /api/device/action
        if (previewUrl.includes('/device/action')) {
          setUrlPreview(
            `${previewUrl} (will be transformed to ${previewUrl.replace('/device/action', '/api/device/action')})`
          );
        } else {
          setUrlPreview(previewUrl);
        }
      } else {
        setUrlPreview('');
      }
    } catch (error) {
      console.error('Error generating URL preview:', error);
    }
  }, [ipAddress, restPath, useHttps]);

  // Advanced mode removed

  // Load saved commands from localStorage on component mount (migrates from legacy key if needed)
  useEffect(() => {
    try {
      // Prefer new key first
      const savedNew = localStorage.getItem(SAVED_COMMANDS_STORAGE_KEY);
      if (savedNew) {
        setMacros(JSON.parse(savedNew));
      } else {
        // Fallback to legacy key and migrate forward
        const savedLegacy = localStorage.getItem(MACROS_STORAGE_KEY);
        if (savedLegacy) {
          setMacros(JSON.parse(savedLegacy));
          try {
            localStorage.setItem(SAVED_COMMANDS_STORAGE_KEY, savedLegacy);
          } catch (e) {
            console.warn('Failed to migrate saved commands to new storage key:', e);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load macros from localStorage:', error);
    }
  }, []);

  // Load registry models on mount
  useEffect(() => {
    (async () => {
      try {
        const models = await getRegistryModels();
        setRegistryModels(models);
      } catch (e) {
        console.error('Failed to load registry models', e);
      }
    })();
  }, []);

  // When model changes, load actions for model
  useEffect(() => {
    (async () => {
      setModelActions([]);
      setSelectedActionKey('');
      setSelectedActionMeta(null);
      if (!selectedModel) return;
      try {
        const actions = await getRegistryActionsByModel(selectedModel);
        // Sort by title then key for stable UI
        actions.sort((a, b) => (a.title || a.key || '').localeCompare(b.title || b.key || ''));
        setModelActions(actions);
      } catch (e) {
        console.error('Failed to load actions for model', selectedModel, e);
      }
    })();
  }, [selectedModel]);

  // When action key changes, set selected meta
  useEffect(() => {
    if (!selectedActionKey) {
      setSelectedActionMeta(null);
      return;
    }
    const found = modelActions.find(a => a.key === selectedActionKey);
    setSelectedActionMeta(found || null);
  }, [selectedActionKey, modelActions]);

  // Save commands to localStorage whenever they change (write to both keys for compatibility)
  useEffect(() => {
    try {
      const json = JSON.stringify(macros);
      localStorage.setItem(SAVED_COMMANDS_STORAGE_KEY, json);
      localStorage.setItem(MACROS_STORAGE_KEY, json);
    } catch (error) {
      console.error('Failed to save macros to localStorage:', error);
    }
  }, [macros]);

  // Subscribe to MQTT result topic when agentId changes
  useEffect(() => {
    // Only subscribe if agentId is valid
    if (!agentId) return;

    // Format agent ID properly for MQTT
    const formattedAgentId = agentId.startsWith('agent-') ? agentId : `agent-${agentId}`;

    // Set up subscription
    const setupSubscription = async () => {
      try {
        // Clean up previous subscription if any
        if (resultSubscription) {
          await resultSubscription();
        }

        console.log(`Setting up MQTT result subscription for ${formattedAgentId}`);

        // Subscribe to the agent's result topic
        const unsubscribe = await subscribeToAgentResults(formattedAgentId, resultData => {
          console.log('Received MQTT result:', resultData);

          // Display the result with appropriate toast
          if (resultData && typeof resultData === 'object' && resultData.data) {
            const mqttData = resultData.data;
            console.log('Processing MQTT data:', mqttData);

            // If we are running a probe, try to match by commandId first
            try {
              const currentMap = probeMapRef.current || { active: false, map: {} };
              if (currentMap.active && mqttData.commandId && currentMap.map[mqttData.commandId] !== undefined) {
                const idx = currentMap.map[mqttData.commandId];
                setProbeResults(prev => {
                  const next = [...prev];
                  next[idx] = {
                    ...next[idx],
                    status: mqttData.status,
                    response: mqttData.responseBody || mqttData.response || {},
                    receivedAt: new Date().toISOString(),
                  };
                  return next;
                });
                // If all probes resolved, deactivate session
                setTimeout(() => {
                  const allDone = (probeMapRef.current?.active && probeResults.length > 0)
                    ? probeResults.every(pr => pr.status && pr.status !== 'pending')
                    : false;
                  if (allDone) {
                    probeMapRef.current = { active: false, map: {} };
                    setProbeSessionActive(false);
                    setProbeRunning(false);
                  }
                }, 0);
                return; // Do not treat as generic result
              }
            } catch (e) {
              console.warn('Probe correlation error:', e);
            }

            // Check if this is a result for our last command
            const isOurCommand = !lastCommandId || mqttData.commandId === lastCommandId;

            if (isOurCommand) {
              // Create a properly formatted result object
              const formattedResult = {
                commandId: mqttData.commandId,
                status: mqttData.status,
                // Handle both response and responseBody field names
                response: mqttData.responseBody || mqttData.response || {},
              };

              // Update response state with the result data
              setResponse(prevResponse => ({
                ...(prevResponse || {}),
                mqttResult: formattedResult,
              }));

              // Show success or error toast based on status
              if (mqttData.status === 200) {
                toast.success(
                  <div>
                    <p>
                      <strong>MQTT Result Received</strong>
                    </p>
                    <p className="text-xs">Command ID: {mqttData.commandId?.substring(0, 8)}...</p>
                  </div>
                );
              } else {
                toast.error(
                  <div>
                    <p>
                      <strong>MQTT Error Result</strong>
                    </p>
                    <p className="text-xs">Status: {mqttData.status}</p>
                    <p className="text-xs">Command ID: {mqttData.commandId?.substring(0, 8)}...</p>
                  </div>
                );
              }
            }
          }
        });

        // Store unsubscribe function
        setResultSubscription(() => unsubscribe);
      } catch (error) {
        console.error('Failed to subscribe to MQTT result topic:', error);
        toast.error('Failed to subscribe to agent result topic');
      }
    };

    setupSubscription();

    // Clean up subscription on unmount or when agentId changes
    return () => {
      if (resultSubscription) {
        resultSubscription().catch(err => {
          console.error('Error unsubscribing from MQTT:', err);
        });
      }
    };
  }, [agentId, lastCommandId]); // Re-subscribe when agentId changes

  // Save form data on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          agentId,
          method,
          ipAddress,
          restPath,
          action,
          parameters,
          authMethod,
          authUsername,
          authPassword,
          nonce,
          timestamp,
          useHttps,
          verify,
          selectedTemplate,
        })
      );
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }, [
    agentId,
    method,
    ipAddress,
    restPath,
    action,
    parameters,
    authMethod,
    authUsername,
    authPassword,
    nonce,
    timestamp,
    useHttps,
    verify,
    selectedTemplate,
  ]);

  // Load saved form data
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const data = JSON.parse(savedData);
        setAgentId(data.agentId || '');
        setMethod(data.method || 'POST');
        setIpAddress(data.ipAddress || '');
        setRestPath(data.restPath || '');
        setAuthMethod(data.authMethod || 'standard');
        setAuthUsername(data.authUsername || '');
        setAuthPassword(data.authPassword || '');
        setNonce(data.nonce || '');
        setTimestamp(data.timestamp || '');
        setUseHttps(data.useHttps !== undefined ? data.useHttps : true);
        setVerify(data.verify !== undefined ? data.verify : false);
        setAction(data.action || '');
        setParameters(
          data.parameters && data.parameters.length > 0
            ? data.parameters
            : [{ id: Date.now(), key: '', value: '' }]
        );
        setSelectedTemplate(data.selectedTemplate || 'none');
      }
    } catch (error) {
      console.error('Failed to load form data from localStorage:', error);
      toast.error('Could not load saved form data.');
    }
  }, []);

  const handleAddParameter = () => {
    setParameters([...parameters, { id: Date.now(), key: '', value: '' }]);
  };

  const handleRemoveParameter = id => {
    setParameters(parameters.filter(p => p.id !== id));
  };

  const handleParameterChange = (id, field, value) => {
    setParameters(parameters.map(p => (p.id === id ? { ...p, [field]: value } : p)));
  };

  // Helper function to generate MD5 hash for content
  const generateContentMD5 = content => {
    if (!content || Object.keys(content).length === 0) return '';
    // Convert content to JSON string if it's an object
    const contentString = typeof content === 'string' ? content : JSON.stringify(content);
    // Generate MD5 hash using crypto-js
    const md5Hash = CryptoJS.MD5(contentString).toString();
    console.log(`Generated MD5 for content: ${md5Hash}`);
    return md5Hash;
  };

  // Helper function to generate HMAC-SHA256
  const generateHMAC = (secret, input) => {
    if (!secret || !input) {
      console.error('Secret or input missing for HMAC generation');
      return '';
    }
    // Generate HMAC using crypto-js
    const hmac = CryptoJS.HmacSHA256(input, secret).toString();
    console.log(`Generated HMAC with input: ${input}`);
    return hmac;
  };

  const handleSendCommand = async () => {
    if (!agentId) {
      toast.error('Agent ID is required.');
      return;
    }
    if (!ipAddress) {
      toast.error('IP Address is required.');
      return;
    }
    // restPath can be empty, e.g. for calls to IP root

    setLoading(true);
    setResponse(null);

    // Use HTTPS if specified, otherwise use HTTP
    const protocol = useHttps ? 'https' : 'http';
    const constructedUrl =
      `${protocol}://${ipAddress}${restPath.startsWith('/') ? restPath : '/' + restPath}`.replace(
        /\/+$/,
        ''
      ); // Ensure leading slash and remove trailing

    let payload = {};

      // User-friendly mode
      const bodyParams = {};
      parameters.forEach(p => {
        if (p.key) bodyParams[p.key] = p.value;
      });

      let constructedBody = {};
      if (method === 'POST' || method === 'PUT') {
        constructedBody = {
          action: action,
          parameters: bodyParams,
        };
      }

      // Prepare authentication based on selected method
      let auth = {};

      if (authMethod === 'standard') {
        // Standard authentication (HMAC SHA-256)
        const currentTimestamp = timestamp || Math.floor(Date.now() / 1000).toString();
        const currentNonce = nonce || Math.floor(Math.random() * 100000).toString();

        // Generate content MD5 for body if needed
        const contentType = 'application/json';
        const contentMD5 = generateContentMD5(constructedBody);

        // Create HMAC input string following the format in Algo API Reference:
        // [request_method]:[request_uri]:[content_md5]:[content_type]:[timestamp]:[nonce]
        const requestUri = restPath.startsWith('/') ? restPath : '/' + restPath;
        const hmacInput = `${method}:${requestUri}:${contentMD5}:${contentType}:${currentTimestamp}:${currentNonce}`;

        // Generate HMAC with password as secret key and hmacInput as input
        const hmac = generateHMAC(authPassword, hmacInput);

        auth = {
          type: 'standard',
          username: authUsername,
          hmac: hmac,
          timestamp: currentTimestamp,
          nonce: currentNonce,
          contentMD5: contentMD5,
          contentType: contentType,
        };

        // Update state if we generated values
        if (!timestamp) setTimestamp(currentTimestamp);
        if (!nonce) setNonce(currentNonce);
      } else if (authMethod === 'basic') {
        // Basic authentication - send username and password directly instead of base64 encoding
        auth = {
          type: 'basic',
          username: authUsername,
          password: authPassword,
        };
      } else {
        // No authentication
        auth = {
          type: 'none',
        };
      }

      // Ensure we're sending the payload in the format the backend expects
      payload = {
        agentId: agentId.startsWith('agent-') ? agentId : `agent-${agentId}`,
        method,
        url: constructedUrl,
        auth,
        body: constructedBody,
        useHttps: useHttps,
        verify: verify,
      };

      // Always add Content-Type header for requests with bodies
      if (method === 'POST' || method === 'PUT') {
        payload.headers = payload.headers || {};
        payload.headers['Content-Type'] = 'application/json';
      }

      // Log the payload for debugging
      console.log('Sending payload:', JSON.stringify(payload, null, 2));

    try {
      // Use postUnauthenticated instead of post for the /test/send-rest-call endpoint
      // This ensures no auth token is sent with the request
      const result = await backendApi.postUnauthenticated('/test/send-rest-call', payload);
      setResponse({
        message: 'Command dispatched successfully. The agent will execute this asynchronously.',
        commandId: result.commandId,
      });
      toast.success('Command dispatched successfully!');
    } catch (error) {
      console.error('Failed to send REST command:', error);

      // Extract error details more thoroughly
      let errorMessage;
      if (error.response) {
        // If we have a response object with data
        if (typeof error.response === 'object') {
          errorMessage = JSON.stringify(error.response, null, 2);
        } else {
          errorMessage = String(error.response);
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = 'An unknown error occurred.';
      }

      setResponse({
        error: 'Failed to dispatch command.',
        details: errorMessage,
        status: error.status || 'Unknown',
      });

      toast.error(
        `Dispatch failed: ${errorMessage.substring(0, 100)}${errorMessage.length > 100 ? '...' : ''}`
      );
    } finally {
      setLoading(false);
    }
  };
  // Device Info Query Handler
  const handleQueryDeviceInfo = async () => {
    if (!agentId) {
      toast.error('Agent ID is required');
      return;
    }
    if (!ipAddress) {
      toast.error('IP Address is required');
      return;
    }

    setLoading(true);
    setResponse(null);

    // Prepare request payload
    const payload = {
      agentId: agentId.startsWith('agent-') ? agentId : `agent-${agentId}`,
      ipAddress: ipAddress,
    };

    try {
      // Make API call using postUnauthenticated
      const result = await backendApi.postUnauthenticated('/test/get-device-info', payload);

      // Store the command ID if available for matching with MQTT results
      if (result && result.commandId) {
        setLastCommandId(result.commandId);
      }

      setResponse({
        message: 'Device info retrieved successfully',
        deviceInfo: result,
      });

      toast.success('Device info retrieved successfully!');
    } catch (error) {
      console.error('Failed to retrieve device info:', error);

      // Extract error details
      let errorMessage;
      if (error.response) {
        if (typeof error.response === 'object') {
          errorMessage = JSON.stringify(error.response, null, 2);
        } else {
          errorMessage = String(error.response);
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = 'An unknown error occurred.';
      }

      setResponse({
        error: 'Failed to retrieve device info',
        details: errorMessage,
        status: error.status || 'Unknown',
      });

      toast.error(
        `Device info query failed: ${errorMessage.substring(0, 100)}${errorMessage.length > 100 ? '...' : ''}`
      );
    } finally {
      setLoading(false);
    }
  };

  // Run a set of probes against common Algo endpoints for the current device
  const handleProbeEndpoints = async () => {
    if (!agentId) {
      toast.error('Agent ID is required');
      return;
    }
    if (!ipAddress) {
      toast.error('IP Address is required');
      return;
    }

    // Build candidate endpoints. Always include current REST Path first if provided.
    const candidates = [];
    const normalizedPath = (p => {
      if (!p) return '';
      return p.startsWith('/') ? p : `/${p}`;
    })(restPath || '');

    if (normalizedPath) {
      candidates.push({
        name: 'Current Path',
        method,
        path: normalizedPath,
        body: method === 'POST' || method === 'PUT'
          ? {
              action,
              parameters: Object.fromEntries(
                parameters.filter(x => x.key).map(x => [x.key, x.value])
              ),
            }
          : undefined,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    // Common test endpoints observed on Algo devices
    candidates.push(
      { name: 'controls.test.start', method: 'POST', path: '/api/controls/test/start', body: {}, headers: { 'Content-Type': 'application/json' } },
      { name: 'ringer.test', method: 'POST', path: '/api/v1/ringer/test', body: { duration_ms: 2000 }, headers: { 'Content-Type': 'application/json' } },
      { name: 'tone.play', method: 'POST', path: '/api/v1/tone/play', body: { tone_id: 'confirm', duration_sec: 5 }, headers: { 'Content-Type': 'application/json' } },
      { name: 'info.status', method: 'GET', path: '/api/info/status', body: undefined, headers: { 'Content-Type': 'application/json' } }
    );

    // Prepare auth block based on current selection
    let authBlock = undefined;
    if (authMethod === 'basic') {
      authBlock = { type: 'basic', username: authUsername, password: authPassword };
    } else if (authMethod === 'standard') {
      // Preserve Standard if user selected it
      authBlock = { type: 'standard', username: authUsername, password: authPassword };
    }

    // Initialize probe session state
    setProbeRunning(true);
    setProbeSessionActive(true);
    const initialResults = candidates.map(c => ({
      name: c.name,
      method: c.method,
      path: c.path,
      url: `${useHttps ? 'https' : 'http'}://${ipAddress}${c.path}`,
      status: 'pending',
      commandId: null,
      response: null,
      sentAt: new Date().toISOString(),
    }));
    setProbeResults(initialResults);

    const map = {};
    try {
      for (let i = 0; i < candidates.length; i++) {
        const c = candidates[i];
        const payload = {
          agentId: agentId.startsWith('agent-') ? agentId : `agent-${agentId}`,
          method: c.method,
          url: `${useHttps ? 'https' : 'http'}://${ipAddress}${c.path}`.replace(/\/+$/, ''),
          headers: c.headers || { 'Content-Type': 'application/json' },
          body: c.body,
          auth: authBlock,
          useHttps,
          verify,
        };

        try {
          const result = await backendApi.postUnauthenticated('/test/send-rest-call', payload);
          const cmdId = result && result.commandId ? result.commandId : null;
          map[cmdId || `idx-${i}`] = i; // fallback key
          setProbeResults(prev => {
            const next = [...prev];
            next[i] = { ...next[i], commandId: cmdId || `idx-${i}` };
            return next;
          });
        } catch (err) {
          // Immediate dispatch failure
          setProbeResults(prev => {
            const next = [...prev];
            next[i] = { ...next[i], status: 'dispatch_failed', response: (err && (err.message || err.toString())) || 'Dispatch error' };
            return next;
          });
        }
      }

      // Activate correlation map so MQTT results can be attached
      probeMapRef.current = { active: true, map };
      toast.success('Probe commands dispatched. Awaiting MQTT results...');
    } catch (e) {
      console.error('Probe execution error:', e);
      toast.error('Probe failed to start');
      setProbeRunning(false);
      setProbeSessionActive(false);
      probeMapRef.current = { active: false, map: {} };
    }
  };

  // Load preset function to populate form fields with selected preset
  const loadPreset = preset => {
    try {
      // setIsAdvancedMode(true); // Switch to advanced mode for proper display

      // Set all the form fields from the preset data
      setMethod(preset.method || 'POST');
      setRestPath(preset.restPath || '');
      setAuthMethod(preset.authMethod || 'basic');
      // Just fill or keep current IP address - no prompts
      if (preset.ipAddress) {
        setIpAddress(preset.ipAddress);
      }
      // If IP isn't provided in preset, we'll leave the current value as is

      // Extract action/parameters from rawBody if present in preset for convenience
      if (preset.rawBody && preset.rawBody.action) {
        setAction(preset.rawBody.action);
      }
      if (preset.rawBody && preset.rawBody.parameters) {
        const params = Object.entries(preset.rawBody.parameters).map(([key, value], index) => ({
          id: Date.now() + index,
          key,
          value: typeof value === 'string' ? value : JSON.stringify(value),
        }));
        setParameters(params.length > 0 ? params : [{ id: Date.now(), key: '', value: '' }]);
      }

      toast.success(`Loaded preset: ${preset.label}`);
    } catch (error) {
      console.error('Error loading preset:', error);
      toast.error('Failed to load preset configuration');
    }
  };

  const handleClearForm = () => {
    if (window.confirm('Are you sure you want to clear the form?')) {
      // Clear basic fields
      setAgentId('');
      setMethod('POST');
      setIpAddress('');
      setRestPath('');
      setAction('');
      setParameters([{ id: Date.now(), key: '', value: '' }]);

      // Clear authentication settings
      setAuthMethod('standard');
      setAuthUsername('');
      setAuthPassword('');
      setNonce('');
      setTimestamp('');

      // Reset SSL/TLS options
      setUseHttps(true);
      setVerify(false);

      // Clear response
      setResponse(null);

      // Do not reset isAdvancedMode by default, user might want to keep the mode
      // setIsAdvancedMode(false);

      toast.success('Form cleared.');
    }
  };

  // Save the current form state as a saved command (UI label change only)
  const handleSaveAsMacro = () => {
    // Generate unique ID for the macro
    const macroId = `macro-${Date.now()}`;

    // Create macro object with current form values
    const newMacro = {
      id: macroId,
      label: 'New Command',
      timestamp: new Date().toISOString(),
      // Form fields
      agentId,
      method,
      ipAddress,
      restPath,
      authMethod,
      authUsername,
      authPassword,
      useHttps,
      verify,
      action,
      parameters: parameters.filter(p => p.key.trim() !== ''),
    };
    // Add new macro to state
    setMacros([...macros, newMacro]);
    toast.success('Command saved.');

    // Open modal and start editing the name
    setIsModalOpen(true);
    setEditingMacroId(macroId);
    setEditedMacroName('New Command');
    // Focus the input after the modal is open
    setTimeout(() => {
      if (macroNameInputRef.current) {
        macroNameInputRef.current.focus();
        macroNameInputRef.current.select();
      }
    }, 100);
  };

  // Load a macro into the form
  const handleLoadMacro = macro => {
    try {
      // Update all form fields with macro values
      setAgentId(macro.agentId || '');
      setMethod(macro.method || 'POST');
      setIpAddress(macro.ipAddress || '');
      setRestPath(macro.restPath || '');
      setAuthMethod(macro.authMethod || 'standard');
      setAuthUsername(macro.authUsername || '');
      setAuthPassword(macro.authPassword || '');
      setUseHttps(macro.useHttps !== undefined ? macro.useHttps : true);
      setVerify(macro.verify !== undefined ? macro.verify : false);

      // Handle action and parameters if they exist
      setAction(macro.action || '');
      if (macro.parameters && macro.parameters.length > 0) {
        const newParams = macro.parameters.map(param => ({
          id: Date.now() + Math.random(),
          key: param.key,
          value: param.value,
        }));
        setParameters(newParams);
      } else {
        setParameters([{ id: Date.now(), key: '', value: '' }]);
      }

      // Advanced mode removed; ignore raw headers/body in saved macros if present

      toast.success(`Loaded command: ${macro.label}`);
      setIsModalOpen(false); // Close modal after loading
    } catch (error) {
      console.error('Error loading macro:', error);
      toast.error('Failed to load command');
    }
  };

  // Delete a macro
  const handleDeleteMacro = macroId => {
    if (window.confirm('Are you sure you want to delete this saved command?')) {
      setMacros(macros.filter(m => m.id !== macroId));
      toast.success('Command deleted');
    }
  };

  // Save edited macro name
  const handleSaveMacroName = () => {
    if (editingMacroId && editedMacroName.trim() !== '') {
      setMacros(
        macros.map(macro =>
          macro.id === editingMacroId ? { ...macro, label: editedMacroName.trim() } : macro
        )
      );
      setEditingMacroId(null);
      setEditedMacroName('');
      toast.success('Command name updated');
    }
  };

  // Cancel editing macro name
  const handleCancelEditMacroName = () => {
    setEditingMacroId(null);
    setEditedMacroName('');
  };

  // Start editing a macro name
  const handleStartEditMacroName = macro => {
    setEditingMacroId(macro.id);
    setEditedMacroName(macro.label);
    // Focus the input after setting the state
    setTimeout(() => {
      if (macroNameInputRef.current) {
        macroNameInputRef.current.focus();
        macroNameInputRef.current.select();
      }
    }, 100);
  };

  const commonInputsDisabled = loading;
  

  const handleApplyRegistryAction = () => {
    if (!selectedActionMeta) return;
    const m = (selectedActionMeta.method || 'POST').toUpperCase();
    setMethod(m);
    setRestPath(selectedActionMeta.pathTemplate || '');
    const d = selectedActionMeta.defaults || {};
    if (d && typeof d === 'object' && d.action) {
      setAction(d.action);
      const paramsObj = d.parameters && typeof d.parameters === 'object' ? d.parameters : {};
      const params = Object.entries(paramsObj).map(([key, value], index) => ({
        id: Date.now() + index,
        key,
        value: typeof value === 'string' ? value : JSON.stringify(value),
      }));
      setParameters(params.length > 0 ? params : [{ id: Date.now(), key: '', value: '' }]);
    } else {
      setAction('');
      setParameters([{ id: Date.now(), key: '', value: '' }]);
    }
    toast.success('Applied registry action to form');
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4 border-b pb-2">
        <div className="flex justify-between ">
          

          <button type="button" onClick={handleClearForm} disabled={loading} className="btn-danger btn-secondary ml-12 px-3 py-1 bg-red-200 text-gray-700 rounded hover:bg-gray-300">
            Clear
          </button>

          <button
            type="button"
            onClick={handleQueryDeviceInfo}
            disabled={loading}
            className="btn-secondary ml-12 px-3 py-1 bg-blue-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Query
          </button>

          <button
            type="button"
            onClick={handleSaveAsMacro}
            disabled={loading}
            className="btn-secondary flex items-center ml-12 px-3 py-1 bg-blue-200 text-gray-700 rounded hover:bg-gray-300"
          >
            <FiSave className="mr-1" /> Save Command
          </button>

          <button
            type="button"
            onClick={handleProbeEndpoints}
            disabled={loading || probeRunning || !agentId || !ipAddress}
            className="btn-secondary ml-12 px-3 py-1 bg-blue-200 text-gray-700 rounded hover:bg-gray-300"
          >
            {probeRunning ? 'Probing…' : 'Probe'}
          </button>

          <button
            type="button"
            onClick={handleSendCommand}
            disabled={loading || !agentId || !ipAddress}
            className="flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed min-w-[150px] ml-12 px-3 py-1 bg-blue-200 text-gray-700 rounded hover:bg-gray-300"
          >
            {loading && (
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {loading ? 'Dispatching...' : 'Dispatch'}
          </button>


        </div>
      </div>

      {/* User-Friendly Mode Specific Inputs */}
            <div className="space-y-4 mb-4">
              {/* Registry Helpers */}
              {/* <div className="border-t pt-4"> */}
                <h3 className="text-md font-semibold text-gray-800 mb-3">Registry Helpers</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <select
                      value={selectedModel}
                      onChange={e => setSelectedModel(e.target.value)}
                      className="w-full input-class"
                      disabled={loading}
                    >
                      <option value="">Select model…</option>
                      {registryModels.map(m => (
                        <option key={m.model} value={m.model}>
                          {m.model} {m.displayNumber ? `- ${m.displayNumber}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                    <select
                      value={selectedActionKey}
                      onChange={e => setSelectedActionKey(e.target.value)}
                      className="w-full input-class"
                      disabled={loading || !selectedModel || modelActions.length === 0}
                    >
                      <option value="">Select action…</option>
                      {modelActions.map(a => (
                        <option key={a.key} value={a.key}>
                          {a.title || a.key}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleApplyRegistryAction}
                      disabled={!selectedActionMeta || loading}
                      className="px-3 py-1 bg-blue-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Apply to Form
                    </button>
                  </div>
                </div>
                {selectedActionMeta && (
                  <div className="mt-3 text-xs text-gray-700 space-y-1">
                    <div><span className="font-semibold">Method:</span> {selectedActionMeta.method}</div>
                    <div><span className="font-semibold">Path:</span> {selectedActionMeta.pathTemplate}</div>
                    {selectedActionMeta.defaults && (
                      <div className="mt-2">
                        <div className="font-semibold mb-1">Defaults</div>
                        <pre className="bg-white border rounded p-2 max-h-40 overflow-auto">{JSON.stringify(selectedActionMeta.defaults, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                )}
              {/* </div> */}
              <div className="border-t pt-2">
                <h3 className="text-md font-semibold text-gray-800 mb-0">Autentication</h3>
               
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 w-full">
                  {/* Method select only, no label */}
                  <div className="flex flex-col ">
                  <label 
                    htmlFor="authMethod" 
                    className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Method</label>
                    <select
                      id="authMethod"
                      value={authMethod}
                      onChange={e => setAuthMethod(e.target.value)}
                      disabled={loading}
                      className="w-full input-class"
                    >
                      <option value="standard">Standard (HMAC SHA-256)</option>
                      <option value="basic">Basic</option>
                      <option value="none">None</option>
                    </select>
                  </div>
                  {/* Username */}
                  <div>
                    <label
                      htmlFor="authUsername"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Username {authMethod !== 'none' && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      id="authUsername"
                      value={authUsername}
                      onChange={e => setAuthUsername(e.target.value)}
                      disabled={loading || authMethod === 'none'}
                      className="w-full input-class"
                      placeholder="Enter username"
                      required={authMethod !== 'none'}
                    />
                  </div>
                  {/* Password */}
                  <div>
                    <label
                      htmlFor="authPassword"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Password {authMethod !== 'none' && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="password"
                      id="authPassword"
                      value={authPassword}
                      onChange={e => setAuthPassword(e.target.value)}
                      disabled={loading || authMethod === 'none'}
                      className="w-full input-class"
                      placeholder="********"
                      required={authMethod !== 'none'}
                    />
                  </div>
                  {/* Nonce */}
                  <div className={authMethod === 'standard' ? '' : 'opacity-0 pointer-events-none select-none'}>
                    <label
                      htmlFor="nonce"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Nonce
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        id="nonce"
                        value={nonce}
                        onChange={e => setNonce(e.target.value)}
                        disabled={loading || authMethod !== 'standard'}
                        className="w-full input-class"
                        placeholder="e.g., 49936"
                      />
                      <button
                        type="button"
                        onClick={() => setNonce(Math.floor(Math.random() * 100000).toString())}
                        disabled={loading || authMethod !== 'standard'}
                        className="ml-2 px-3 py-1 bg-blue-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Generate
                      </button>
                    </div>
                  </div>
                  {/* Timestamp */}
                  <div className={authMethod === 'standard' ? '' : 'opacity-0 pointer-events-none select-none'}>
                    <label
                      htmlFor="timestamp"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Timestamp (Unix)
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        id="timestamp"
                        value={timestamp}
                        onChange={e => setTimestamp(e.target.value)}
                        disabled={loading || authMethod !== 'standard'}
                        className="w-full input-class"
                        placeholder="e.g., 1601312252"
                      />
                      <button
                        type="button"
                        onClick={() => setTimestamp(Math.floor(Date.now() / 1000).toString())}
                        disabled={loading || authMethod !== 'standard'}
                        className="ml-2 px-3 py-1 bg-blue-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Now
                      </button>
                    </div>
                  </div>
                </div>

               

                {/* SSL/TLS Options */}
                <div className="mt-4 border-t pt-4">
                  <h3 className="text-md font-semibold text-gray-800 mb-3">SSL/TLS Options</h3>
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center">
                      <input
                        id="useHttps"
                        type="checkbox"
                        checked={useHttps}
                        onChange={e => setUseHttps(e.target.checked)}
                        disabled={loading}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="useHttps" className="ml-2 block text-sm text-gray-700">
                        Force HTTPS
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="verify"
                        type="checkbox"
                        checked={verify}
                        onChange={e => setVerify(e.target.checked)}
                        disabled={loading}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="verify" className="ml-2 block text-sm text-gray-700">
                        Verify SSL Certificate
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {(method === 'POST' || method === 'PUT') && (
                <div className="border-t pt-4">
                  <button
                    type="button"
                    className="flex items-center text-md font-semibold text-gray-800 mb-3 focus:outline-none"
                    onClick={() => setShowRequestBody(prev => !prev)}
                    aria-expanded={showRequestBody}
                    aria-controls="request-body-section"
                  >
                    <span className="mr-2">Request Body</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${showRequestBody ? 'transform rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                  {showRequestBody && (
                    <div id="request-body-section">
                      <div>
                        <label
                          htmlFor="action"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Action
                        </label>
                        <input
                          type="text"
                          id="action"
                          value={action}
                          onChange={e =>
                            setAction(
                              e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1)
                            )
                          }
                          disabled={loading}
                          className="w-full input-class"
                          placeholder="e.g., QueryStatus"
                        />
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Parameters
                        </label>
                        {parameters.map((param, index) => (
                          <div key={param.id} className="flex items-center space-x-2 mb-2">
                            <input
                              type="text"
                              value={param.key}
                              onChange={e => handleParameterChange(param.id, 'key', e.target.value)}
                              placeholder="Key"
                              disabled={loading}
                              className="w-1/3 input-class"
                            />
                            <input
                              type="text"
                              value={param.value}
                              onChange={e =>
                                handleParameterChange(param.id, 'value', e.target.value)
                              }
                              placeholder="Value"
                              disabled={loading}
                              className="w-1/2 input-class"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveParameter(param.id)}
                              disabled={loading || parameters.length === 1}
                              className="text-red-500 hover:text-red-700 disabled:opacity-50"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={handleAddParameter}
                          disabled={loading}
                          className="mt-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          + Add Parameter
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

          {/* Probe Results */}
          {probeResults && probeResults.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h3 className="text-lg font-semibold mb-2">Probe Results</h3>
              <div className="space-y-3">
                {probeResults.map((pr, idx) => (
                  <div key={pr.commandId || pr.url || idx} className="bg-gray-50 border rounded p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">
                        {pr.name} <span className="ml-2 text-gray-500 font-normal">[{pr.method}] {pr.path}</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs ${pr.status === 200 ? 'bg-green-100 text-green-800' : (pr.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800')}`}>
                        {String(pr.status)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1 break-all">URL: {pr.url}</div>
                    {pr.commandId && (
                      <div className="text-xs text-gray-600">Command ID: <span className="font-mono">{pr.commandId}</span></div>
                    )}
                    {pr.response && (
                      <pre className="mt-2 p-2 bg-white border rounded text-xs max-h-48 overflow-auto">
                        {typeof pr.response === 'object' ? JSON.stringify(pr.response, null, 2) : String(pr.response)}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

      {/* Action Buttons */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex flex-wrap gap-5 justify-between">
          




        </div>

 
      </div>

      {/* Main container with form and presets side-by-side */}
      <div className="flex">
        {/* Form Elements */}
        <div className="flex-grow mr-4">
          <div className="grid grid-cols-4 md:grid-cols-4 gap-x-4 gap-y-4 mb-4">
            <div>
              <label htmlFor="agentId" className="block text-sm font-medium text-gray-700 mb-1">
                Agent ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="agentId"
                value={agentId}
                onChange={e => setAgentId(e.target.value)}
                disabled={commonInputsDisabled}
                className="w-full input-class"
                placeholder="Unique ID for tracking"
              />
            </div>
            <div>
              <label htmlFor="method" className="block text-sm font-medium text-gray-700 mb-1">
                HTTP Method
              </label>
              <select
                id="method"
                value={method}
                onChange={e => setMethod(e.target.value)}
                disabled={commonInputsDisabled}
                className="w-full input-class"
              >
                <option>GET</option>
                <option>POST</option>
                <option>PUT</option>
                <option>DELETE</option>
              </select>
            </div>
            <div>
              <label htmlFor="ipAddress" className="block text-sm font-medium text-gray-700 mb-1">
                IP Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="ipAddress"
                value={ipAddress}
                onChange={e => setIpAddress(e.target.value)}
                disabled={commonInputsDisabled}
                required
                className="w-full input-class"
                placeholder="e.g., 203.0.113.10"
              />
            </div>
            <div>
              <label htmlFor="restPath" className="block text-sm font-medium text-gray-700 mb-1">
                REST Path
              </label>
              <input
                type="text"
                id="restPath"
                value={restPath}
                onChange={e => setRestPath(e.target.value)}
                disabled={commonInputsDisabled}
                className="w-full input-class"
                placeholder="e.g., /api/info/status"
              />
              {urlPreview && (
                <p className="mt-1 text-xs text-blue-600">
                  <span className="font-semibold">URL:</span> {urlPreview}
                </p>
              )}
            </div>
          </div>


          {/* Response Display */}
          {response && (
            <div className="mt-6 border-t pt-4">
              <h3 className="text-lg font-semibold mb-2">Dispatch Result</h3>
              <div
                className={`p-4 rounded-md ${response.error ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}
              >
                {/* Command ID Display */}
                {response.commandId && (
                  <div className="mb-3">
                    <span className="font-medium">Command ID:</span>
                    <span className="ml-2 bg-gray-100 px-2 py-1 rounded font-mono">
                      {response.commandId}
                    </span>
                  </div>
                )}

                {/* Device Info Display */}
                {/* {response.deviceInfo && (
              <div className="mb-3">
                <h4 className="font-medium text-base mb-2">Device Information</h4>
                <div className="bg-white p-3 rounded border border-gray-200">
                  {response.deviceInfo.deviceName && (
                    <div className="py-1">
                      <span className="font-medium">Device Name:</span> 
                      <span className="ml-2">{response.deviceInfo.deviceName}</span>
                    </div>
                  )}
                  {response.deviceInfo.macAddress && (
                    <div className="py-1">
                      <span className="font-medium">MAC Address:</span> 
                      <span className="ml-2 font-mono">{response.deviceInfo.macAddress}</span>
                    </div>
                  )}
                  {response.deviceInfo.firmwareVersion && (
                    <div className="py-1">
                      <span className="font-medium">Firmware:</span> 
                      <span className="ml-2">{response.deviceInfo.firmwareVersion}</span>
                    </div>
                  )}
                  {response.deviceInfo.sipStatus && (
                    <div className="py-1">
                      <span className="font-medium">SIP Status:</span> 
                      <span className="ml-2">{response.deviceInfo.sipStatus}</span>
                    </div>
                  )}
                  {response.deviceInfo.model && (
                    <div className="py-1">
                      <span className="font-medium">Model:</span> 
                      <span className="ml-2">{response.deviceInfo.model}</span>
                    </div>
                  )}
                  {response.deviceInfo.status && (
                    <div className="py-1">
                      <span className="font-medium">Status:</span> 
                      <span className="ml-2">{response.deviceInfo.status}</span>
                    </div>
                  )}
                </div>
              </div>
            )} */}

                {/* Full Response Display */}
                {/* <pre className="text-sm whitespace-pre-wrap break-all">
              {JSON.stringify(response, null, 2)}
            </pre> */}

                {/* MQTT Result Display - Enhanced and more prominent */}
                {response.mqttResult && (
                  <div className="mt-4 border-2 border-blue-300 rounded-md p-4 bg-blue-50">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-blue-700">MQTT Agent Result</h3>
                      <span
                        className={`px-3 py-1 rounded-full text-sm ${response.mqttResult.status === 200 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                      >
                        Status: {response.mqttResult.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div className="bg-white p-3 rounded-md shadow-sm">
                        <h4 className="text-sm font-medium text-gray-500 mb-1">Command ID</h4>
                        <div className="font-mono text-sm break-all">
                          {response.mqttResult.commandId}
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-md shadow-sm">
                        <h4 className="text-sm font-medium text-gray-500 mb-1">Received</h4>
                        <div className="text-sm">{new Date().toLocaleTimeString()}</div>
                      </div>
                    </div>

                    {response.mqttResult.response && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Response Data</h4>
                        <div className="bg-white border border-gray-200 rounded-md">
                          {typeof response.mqttResult.response === 'object' ? (
                            <div className="divide-y divide-gray-200">
                              {Object.entries(response.mqttResult.response).map(([key, value]) => (
                                <div key={key} className="flex px-3 py-2">
                                  <span className="w-1/3 font-medium text-gray-700">{key}:</span>
                                  <span className="w-2/3 break-all">
                                    {typeof value === 'object'
                                      ? JSON.stringify(value)
                                      : String(value)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <pre className="p-3 overflow-auto text-sm max-h-60">
                              {JSON.stringify(response.mqttResult.response, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Future Enhancement: Structured Response Viewer */}
                {/* 
            <div className="mt-4 border-t pt-2">
              <h4 className="font-medium text-sm mb-2">Response Timeline</h4>
              <div className="space-y-2">
                <p className="text-xs">Command dispatched at: {new Date().toLocaleString()}</p>
                <p className="text-xs">Status updates will appear here...</p>
              </div>
              <button 
                className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                onClick={() => console.log('Future feature: fetch command status')}
              >
                Check Status
              </button>
            </div>
            */}
              </div>
            </div>
          )}
        </div>

        {/* Quick Commands / Presets Panel */}
        {/* <div className="w-64 bg-gray-50 rounded-md p-3 border border-gray-300 self-start sticky top-4">
          <div className="flex items-center mb-3 pb-2 border-b border-gray-300">
            <FiCommand className="text-blue-500 mr-2" />
            <h3 className="text-sm font-semibold">Quick Commands</h3>
          </div>

          <div className="space-y-1">
            {algoApiPresets.map((preset, index) => (
              <button
                key={index}
                onClick={() => loadPreset(preset)}
                className="w-full text-left text-sm py-2 px-2 hover:bg-blue-50 rounded flex items-center justify-between transition-colors group"
              >
                <span>{preset.label}</span>
                <FiChevronRight className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-500" />
              </button>
            ))}
          </div>

          <div className="mt-4 pt-2 border-t border-gray-300 text-xs text-gray-500">
            <p>
              Click a preset to populate the form with predefined API settings for different Algo
              devices.
            </p>
          </div>
        </div> */}
      </div>

      <style jsx>{`
        .input-class {
          @apply px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500;
        }
        .btn-primary {
          @apply bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md;
        }
        .btn-secondary {
          @apply text-sm text-blue-600 hover:text-blue-800;
        }
        .btn-danger {
          @apply text-sm text-red-600 hover:text-red-800;
        }
      `}</style>

      {/* Saved Commands Management Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl w-full max-w-2xl max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4 pb-2 border-b">
              <h3 className="text-lg font-medium">Saved Commands</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            {macros.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No saved commands yet. Use "Save Command" to create one.
              </p>
            ) : (
              <div className="space-y-3">
                {macros.map(macro => (
                  <div
                    key={macro.id}
                    className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                  >
                    {editingMacroId === macro.id ? (
                      <div className="flex-grow flex items-center">
                        <input
                          ref={macroNameInputRef}
                          type="text"
                          value={editedMacroName}
                          onChange={e => setEditedMacroName(e.target.value)}
                          className="border p-1 flex-grow"
                        />
                        <button
                          onClick={handleSaveMacroName}
                          className="text-green-500 hover:text-green-700 ml-2"
                          title="Save"
                        >
                          <FiCheck />
                        </button>
                        <button
                          onClick={handleCancelEditMacroName}
                          className="text-red-500 hover:text-red-700 ml-2"
                          title="Cancel"
                        >
                          <FiX />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex-grow">
                          <div className="font-medium">{macro.label}</div>
                          <div className="text-xs text-gray-500">
                            {macro.ipAddress} - {macro.method} {macro.restPath}
                          </div>
                        </div>
                        <div className="flex items-center">
                          <button
                            onClick={() => handleStartEditMacroName(macro)}
                            className="text-blue-500 hover:text-blue-700 mx-2"
                            title="Rename"
                          >
                            <FiEdit />
                          </button>
                          <button
                            onClick={() => handleLoadMacro(macro)}
                            className="bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1 rounded text-sm mx-2"
                            title="Load Command"
                          >
                            Load
                          </button>
                          <button
                            onClick={() => handleDeleteMacro(macro.id)}
                            className="text-red-500 hover:text-red-700 ml-2"
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RestCallTestPanel;
